/**
 * Loud local notifications for safety alarms — the ONLY thing that can alert the
 * crew when the phone is locked or the app is backgrounded, because the WebView's
 * banner, haptics and WebAudio siren are all frozen then.
 *
 * These post to a dedicated high-importance Android notification channel with its
 * own alarm sound (android/app/src/main/res/raw/alarm.wav) and vibration, so a
 * dragging anchor or a closing AIS target makes a proper noise screen-off rather
 * than a silent card. Everything is native-only and wrapped so the web build (and
 * the tests) are clean no-ops, and so a denied permission degrades rather than
 * throws.
 */
import { Capacitor } from '@capacitor/core';
import type {
  LocalNotificationsPlugin,
  Channel,
} from '@capacitor/local-notifications';

/** The dedicated alarm channel id. High importance + custom sound + vibration. */
export const ALARM_CHANNEL_ID = 'ardent-safety-alarms';

/**
 * The action-type id every alarm notification carries, and its single "OK" action.
 * Without an action type (and a listener for it) the loud notification had no
 * working acknowledge: tapping it changed nothing and it re-fired on the next fix,
 * so the crew could never clear it. The listener below maps a press back to the
 * alarm's key so the runner can silence that alarm's current episode.
 */
export const ALARM_ACTION_TYPE_ID = 'ardent-safety-alarm-actions';
export const ALARM_ACK_ACTION_ID = 'ack';

/**
 * The alarm-sound resource name. Refers to android/app/src/main/res/raw/alarm.wav;
 * if that raw resource is ever missing the plugin falls back to the default system
 * notification sound, so this never goes silent by accident.
 */
const ALARM_SOUND = 'alarm.wav';

/**
 * Stable notification ids per alarm kind, so re-firing the same standing alarm
 * updates its notification rather than stacking a fresh one every fix. A number
 * the plugin accepts (32-bit int).
 */
export const ALARM_NOTIFICATION_IDS: Record<string, number> = {
  anchor: 90101,
  aisCpa: 90102,
  grounding: 90103,
  offTrack: 90104,
  waypointArrival: 90105,
  generic: 90100,
};

export interface AlarmNotification {
  /** Notification title, e.g. "Anchor dragging". */
  title: string;
  /** Body text, e.g. "42 m from the anchor — outside the swing circle." */
  body: string;
  /** Which stable id to post under (defaults to the generic slot). */
  key?: keyof typeof ALARM_NOTIFICATION_IDS;
}

export interface NotifyDeps {
  /** Injectable for tests; defaults to the real LocalNotifications plugin. */
  plugin?: LocalNotificationsPlugin;
  /** Injectable for tests; defaults to Capacitor.isNativePlatform(). */
  isNative?: boolean;
}

/** Resolve the plugin lazily so nothing native is pulled into web start-up. */
async function resolvePlugin(deps?: NotifyDeps): Promise<LocalNotificationsPlugin | null> {
  if (deps?.plugin) return deps.plugin;
  const native = deps?.isNative ?? Capacitor.isNativePlatform();
  if (!native) return null;
  try {
    const mod = await import('@capacitor/local-notifications');
    return mod.LocalNotifications;
  } catch {
    return null;
  }
}

let channelReady = false;

/**
 * Create the alarm channel once. Idempotent, native-only, never throws. Safe to
 * call on every arm; the OS de-dupes by channel id anyway.
 */
export async function initAlarmNotifications(deps?: NotifyDeps): Promise<void> {
  if (channelReady) return;
  const plugin = await resolvePlugin(deps);
  if (!plugin) return;
  const channel: Channel = {
    id: ALARM_CHANNEL_ID,
    name: 'Safety alarms',
    description: 'Anchor drag, collision and other on-water safety alarms.',
    importance: 5, // IMPORTANCE_HIGH: heads-up + sound, the loudest a channel gets.
    sound: ALARM_SOUND,
    visibility: 1, // show on the lock screen.
    vibration: true,
    lights: true,
    lightColor: '#FF3B30',
  };
  try {
    await plugin.createChannel(channel);
    channelReady = true;
  } catch {
    // Channel creation can fail on odd OEMs; leave channelReady false so a later
    // attempt retries. Firing still works against the default channel behaviour.
  }
  // Register the "OK" action so the alarm notification carries a real acknowledge
  // button. Best-effort and separate from the channel latch: a failure here just
  // means no dedicated button, and a plain tap still acknowledges via the listener.
  try {
    await plugin.registerActionTypes({
      types: [
        {
          id: ALARM_ACTION_TYPE_ID,
          actions: [{ id: ALARM_ACK_ACTION_ID, title: 'OK' }],
        },
      ],
    });
  } catch {
    /* best-effort */
  }
}

export type NotificationPermission = 'granted' | 'denied' | 'unavailable';

/**
 * Ensure POST_NOTIFICATIONS is granted (Android 13+ asks at runtime). Returns
 * 'unavailable' on web / where the plugin is absent, so the caller can treat that
 * the same as "carry on, just no notifications".
 */
export async function ensureNotificationPermission(deps?: NotifyDeps): Promise<NotificationPermission> {
  const plugin = await resolvePlugin(deps);
  if (!plugin) return 'unavailable';
  try {
    const current = await plugin.checkPermissions();
    if (current.display === 'granted') return 'granted';
    const asked = await plugin.requestPermissions();
    return asked.display === 'granted' ? 'granted' : 'denied';
  } catch {
    return 'unavailable';
  }
}

/**
 * Post (or update) a loud alarm notification now. Native-only, never throws.
 * Ensures the channel exists first, so the very first alarm still lands on the
 * high-importance channel.
 */
export async function fireAlarmNotification(n: AlarmNotification, deps?: NotifyDeps): Promise<void> {
  const plugin = await resolvePlugin(deps);
  if (!plugin) return;
  await initAlarmNotifications(deps);
  const id = ALARM_NOTIFICATION_IDS[n.key ?? 'generic'] ?? ALARM_NOTIFICATION_IDS.generic;
  try {
    await plugin.schedule({
      notifications: [
        {
          id,
          title: n.title,
          body: n.body,
          channelId: ALARM_CHANNEL_ID,
          sound: ALARM_SOUND,
          // Carry the acknowledge action so the crew has a real OK button, and a
          // tap or OK routes back to the runner through the action listener.
          actionTypeId: ALARM_ACTION_TYPE_ID,
          // Fire immediately; no schedule.at means "now".
          ongoing: false,
          autoCancel: true,
        },
      ],
    });
  } catch {
    // A denied permission or an OEM quirk: swallow it. The foreground siren and
    // banner still alert when the app is up; this path is best-effort screen-off.
  }
}

/** Clear a previously-posted alarm notification (when the alarm stands down). */
export async function clearAlarmNotification(
  key: keyof typeof ALARM_NOTIFICATION_IDS,
  deps?: NotifyDeps,
): Promise<void> {
  const plugin = await resolvePlugin(deps);
  if (!plugin) return;
  const id = ALARM_NOTIFICATION_IDS[key] ?? ALARM_NOTIFICATION_IDS.generic;
  try {
    await plugin.cancel({ notifications: [{ id }] });
  } catch {
    /* best-effort */
  }
}

/**
 * Wire the notification's OK / tap through to a handler with the alarm's key.
 *
 * This is the piece that was missing: nothing listened for the notification action,
 * so pressing OK did nothing. Both the dedicated OK button and a plain tap on the
 * notification arrive as 'localNotificationActionPerformed'; either counts as an
 * acknowledge, so we map the notification id back to its alarm key and hand that to
 * the caller (the runner marks that alarm acknowledged and clears the notification).
 *
 * Native-only and never throws; returns a remover. On web / where the plugin is
 * absent it's a no-op remover, so callers can wire it unconditionally.
 */
export async function registerAlarmAckListener(
  onAcknowledge: (key: string) => void,
  deps?: NotifyDeps,
): Promise<() => void> {
  const plugin = await resolvePlugin(deps);
  if (!plugin) return () => {};
  const idToKey = new Map<number, string>();
  for (const [key, id] of Object.entries(ALARM_NOTIFICATION_IDS)) idToKey.set(id, key);
  try {
    const handle = await plugin.addListener('localNotificationActionPerformed', (ev) => {
      const key = idToKey.get(ev.notification.id);
      if (key) onAcknowledge(key);
    });
    return () => {
      void handle.remove();
    };
  } catch {
    return () => {};
  }
}

/** Test seam: reset the one-time channel-created latch. */
export function __resetChannelForTest(): void {
  channelReady = false;
}
